import {
  APP_LABELS,
  APP_OPTIONS,
  APP_TAG_COLORS,
  APP_TYPES,
  CATEGORY_LABELS,
  CATEGORY_ORDER,
  LOCALES,
  LegalApp,
  TYPE_CATEGORY,
  appParam,
  deriveTypeStatus,
  formatEffectiveDate,
  formatPublishedAt,
  typeLabel,
  typeSlug,
} from './legalConstants';
import {
  Alert,
  Box,
  Button,
  DataTable,
  PageHeader,
  SegmentedControl,
  Tag,
  Text,
} from '@tocktix/pantry';
import { LegalPageStatusesQuery, useLegalPageStatusesQuery } from 'generated/graphql';
import { hasEngRoleSelector } from 'selectors/rolesSelectors';
import { useActions } from 'utils/connect';
import { useNavigate } from 'react-router-v7';
import { useSelector } from 'react-redux';
import { useState } from 'react';
import isLegalEditorSelector from 'selectors/isLegalEditorSelector';

type StatusRow = LegalPageStatusesQuery['legalPageStatuses'][number];

const EM_DASH = '\u2014';

/**
 * Landing dashboard for the v7 consumer-legal tool. Shows every legal page type as a row and each
 * supported locale as a column, so an editor sees the status and last-published date of every page
 * at a glance. Pick an app (Tock or JCE) — or, once only one app remains, the picker collapses to a
 * label — then jump into the editor from any row. Reviews live on a separate route.
 */
const LegalPagesRoute: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const isLegalEditor = useSelector(isLegalEditorSelector);
  const isEng = useSelector(hasEngRoleSelector);
  const { actions } = useActions();
  const canEdit = isEng || isLegalEditor;

  // Multiple apps still exist (Tock + JCE), so the picker is shown; retiring Tock (dropping it from
  // APP_OPTIONS) collapses the picker to a single-app label with no other changes.
  const hasMultipleApps = APP_OPTIONS.length > 1;
  const [app, setApp] = useState<LegalApp>(APP_OPTIONS[0].value);

  const { data } = useLegalPageStatusesQuery({
    skip: !canEdit,
    variables: { app },
    // Re-query every time this route remounts (e.g. returning from the editor) so a just-created
    // draft/publish shows without a hard refresh. `nextFetchPolicy` keeps the app-picker toggle
    // from spamming the network within the same mount.
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    onError: () => actions.toast.showError("Couldn't load legal page statuses."),
  });

  if (!canEdit) {
    return (
      <Box padding={[400, 500]}>
        <PageHeader title="Legal Pages" />
        <Alert
          color="danger"
          message="You do not have access to this tool."
          data-testid="legal-editor-access-denied"
        />
      </Box>
    );
  }

  // type -> locale -> current rows, so each dashboard cell can derive its own status + date.
  const rowsByTypeAndLocale = new Map<string, Map<string, StatusRow[]>>();
  for (const row of data?.legalPageStatuses ?? []) {
    let byLocale = rowsByTypeAndLocale.get(row.type);
    if (!byLocale) {
      byLocale = new Map<string, StatusRow[]>();
      rowsByTypeAndLocale.set(row.type, byLocale);
    }
    const existing = byLocale.get(row.locale);
    if (existing) {
      existing.push(row);
    } else {
      byLocale.set(row.locale, [row]);
    }
  }

  const cellRows = (type: string, locale: string): StatusRow[] =>
    rowsByTypeAndLocale.get(type)?.get(locale) ?? [];

  /** Last-published date for a cell: prefer the publish timestamp, fall back to the effective date. */
  const publishedDateLabel = (rows: StatusRow[]): string => {
    const publishedRow = rows.find((row) => row.state === 'PUBLISHED');
    if (!publishedRow) {
      return EM_DASH;
    }
    return (
      formatPublishedAt(publishedRow.publishedAt) ??
      formatEffectiveDate(publishedRow.effectiveDate) ??
      EM_DASH
    );
  };

  return (
    <Box padding={[400, 500]} display="flex" flexDirection="column" gap={300}>
      <PageHeader
        title="Legal Pages"
        description="Author and publish legal policies through a draft → review → publish workflow."
      />

      <Box>
        <Button
          label="Review queue"
          variant="outlined"
          color="secondary"
          data-testid="open-reviews-btn"
          onClick={() => navigate(`/legal/${appParam(app)}/reviews`)}
        />
      </Box>

      {hasMultipleApps ? (
        <Box data-testid="legal-app-picker">
          <SegmentedControl
            id="legal-app-picker"
            name="legal-app"
            label="App"
            currentValue={app}
            onChange={(event) => setApp(event.target.value as LegalApp)}
          >
            {APP_OPTIONS.map((option) => (
              <SegmentedControl.Option
                key={option.value}
                id={`legal-app-${option.value}`}
                label={option.label}
                value={option.value}
              />
            ))}
          </SegmentedControl>
        </Box>
      ) : (
        <Box display="flex" alignItems="center" gap={100} data-testid="legal-app-context">
          <Text variant="bodyStrong">Legal pages for:</Text>
          <Tag label={APP_LABELS[app]} color={APP_TAG_COLORS[app]} />
        </Box>
      )}

      <Box display="flex" flexDirection="column" gap={400} data-testid="legal-type-list">
        {CATEGORY_ORDER.map((category) => {
          const typesInCategory = APP_TYPES[app].filter((type) => TYPE_CATEGORY[type] === category);
          if (typesInCategory.length === 0) {
            return null;
          }
          return (
            <Box
              key={category}
              display="flex"
              flexDirection="column"
              gap={100}
              data-testid={`legal-category-${category}`}
            >
              <Text variant="h5" as="h2">
                {CATEGORY_LABELS[category]}
              </Text>
              <DataTable id={`legal-table-${category}`} border>
                <DataTable.HeaderRow>
                  <DataTable.Header id="legalPage" title="Legal page" />
                  {LOCALES.map((locale) => (
                    <DataTable.Header key={locale.value} id={locale.value} title={locale.label} />
                  ))}
                </DataTable.HeaderRow>
                <DataTable.Body>
                  {typesInCategory.map((type, index) => (
                    <DataTable.Row
                      key={type}
                      id={index}
                      index={index}
                      data-testid={`legal-type-${type}`}
                      aria-label={`Edit ${typeLabel(type)}`}
                      onRowClick={() => navigate(`/legal/${appParam(app)}/${typeSlug(type)}`)}
                    >
                      <DataTable.Cell columnId="legalPage">{typeLabel(type)}</DataTable.Cell>
                      {LOCALES.map((locale) => {
                        const rows = cellRows(type, locale.value);
                        const { status, color, pending } = deriveTypeStatus(rows);
                        return (
                          <DataTable.Cell key={locale.value} columnId={locale.value}>
                            <Box display="flex" flexDirection="column" gap={50}>
                              <Tag label={status} color={color} size="sm" />
                              {pending && (
                                <Box data-testid={`legal-pending-${type}-${locale.value}`}>
                                  <Tag label={pending.label} color={pending.color} size="sm" />
                                </Box>
                              )}
                              <Text variant="caption" color="fgSubtle">
                                {publishedDateLabel(rows)}
                              </Text>
                            </Box>
                          </DataTable.Cell>
                        );
                      })}
                    </DataTable.Row>
                  ))}
                </DataTable.Body>
              </DataTable>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default LegalPagesRoute;
